/**
 * O que faz: Dispara um callback quando um marcador entra na área visível de uma lista.
 * Onde usa: Histórico do chat e lista lateral de conversas.
 * Conexões: IntersectionObserver nativo; não consulta banco nem APIs.
 * Tabelas: Nenhuma.
 * Data: 20/08/2026
 */
import { useEffect, useRef, type RefObject } from 'react';

interface UseScrollSentinelParams {
  ativo: boolean;
  aoFicarVisivel: () => void;
  raizRef?: RefObject<Element | null>;
  margemRaiz?: string;
  chaveMontagem?: string | number | boolean | null;
}

export function useScrollSentinel({
  ativo,
  aoFicarVisivel,
  raizRef,
  margemRaiz = '80px',
  chaveMontagem,
}: UseScrollSentinelParams) {
  const sentinelaRef = useRef<HTMLDivElement>(null);
  const aoFicarVisivelRef = useRef(aoFicarVisivel);
  const chamadaEmAndamentoRef = useRef(false);
  const liberarChamadaRef = useRef<number | null>(null);
  aoFicarVisivelRef.current = aoFicarVisivel;

  useEffect(() => {
    const sentinela = sentinelaRef.current;
    // Sem o marcador no DOM (skeleton ou estado vazio) não há o que observar.
    if (!ativo || !sentinela) {
      chamadaEmAndamentoRef.current = false;
      if (liberarChamadaRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(liberarChamadaRef.current);
        liberarChamadaRef.current = null;
      }
      return;
    }

    // Uma mesma interseção pode emitir vários eventos antes do React atualizar
    // isFetchingNextPage; a trava evita chamadas duplicadas no mesmo frame.
    chamadaEmAndamentoRef.current = false;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting) && !chamadaEmAndamentoRef.current) {
          chamadaEmAndamentoRef.current = true;
          aoFicarVisivelRef.current();
          if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            liberarChamadaRef.current = window.requestAnimationFrame(() => {
              chamadaEmAndamentoRef.current = false;
              liberarChamadaRef.current = null;
            });
          } else {
            chamadaEmAndamentoRef.current = false;
          }
        }
      },
      {
        root: raizRef?.current ?? null,
        rootMargin: margemRaiz,
        threshold: 0,
      },
    );

    observador.observe(sentinela);
    return () => {
      observador.disconnect();
      chamadaEmAndamentoRef.current = false;
      if (liberarChamadaRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(liberarChamadaRef.current);
        liberarChamadaRef.current = null;
      }
    };
  }, [ativo, chaveMontagem, margemRaiz, raizRef]);

  return sentinelaRef;
}
